import React, { MutableRefObject, useEffect } from "react";
import { TagSearchField } from "staysure-component-library";

import { LocationToFormContent } from "@/app/common-utils/schema/location-to-page.schema";
import {
  processCountryList,
  getFontClasses,
} from "@/app/common-utils/travelling-to-utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Loader from "@/components/atoms/loader/loader";
import withBrand from "@/components/with-brand";
import { QuestionThree } from "@/config/app-constant";
import useDebounce from "@/hooks/debounce/debounce";
import useGtmPopup from "@/hooks/gtm/use-gtm-popup";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { QuoteCountryType } from "@/types/AddressTypes";
import { OptionListProps } from "@/types/LocationFromType";
import { STCountryArray } from "@/types/QuestionPayloadType";
import scrollInputToTop from "@/utils/input-anchoring";
import {
  handleScrollToRef,
  handleScrollToRefAnyoneVisiting,
} from "@/utils/scrollUtils";

import FCDOCountryAndDuplicateCountryModal from "./fcod-country-and-duplicate-country-modal";
import LocationFromContainer from "./location-from/location-from-container";
import SameCountryModal from "./same-country-modal";
import styles from "./single-trip-location.module.css";
import { useSearchCountry } from "../hooks/use-address-api";
import { useSingleTripLocationHandlers } from "../hooks/use-single-trip-location-handler";
import { useLocationTo } from "../provider/location-to-provider";

type SingleTripLocationProps = {
  quoteLoading: boolean | undefined;
  brand?: "staysure" | "avanti";
  pageContent: LocationToFormContent;
  actionButtonRef: MutableRefObject<HTMLDivElement | null>;
  fromCountryCode: string | undefined;
  setFromCountry: (country: string) => void;
  modifyPayloadToLocation: (
    fromCountryCode: string | undefined,
    countryCode: string | undefined,
    stateCode?: string,
    countryDetailsArray?: STCountryArray
  ) => void;
  setToLocationCountryDetails: (text?: STCountryArray | undefined) => void;
  setIsDisable: React.Dispatch<React.SetStateAction<boolean>>;
  countryArray?: [];
};

function SingleTripLocation(props: Readonly<SingleTripLocationProps>) {
  const {
    quoteLoading,
    brand,
    pageContent,
    actionButtonRef,
    fromCountryCode,
    countryArray,
    setFromCountry,
    modifyPayloadToLocation,
    setToLocationCountryDetails,
    setIsDisable,
  } = props;

  const {
    questionTwoText,
    questionTwoPlaceHolder,
    popularCountryHeaderText,
    popularCountryList,
    fcdoCountryHeaderText,
    sameCountryModalHeaderText,
  } = pageContent;

  const locationFromContent = pageContent?.locationFrom?.["0"];
  const [valueFilter, setValueFilter] = React.useState<string | null>(null);
  const { setSelectedState, setSelectedStateTwo } = useQuestionsState();
  const debouncedSearchTerm = useDebounce(valueFilter, 600);
  const { current } = useWizard();
  const [addresses, loading, addressesList, clearAddressList]: [
    QuoteCountryType[],
    boolean,
    OptionListProps[],
    clearAddressList: () => void,
  ] = useSearchCountry(debouncedSearchTerm);
  const [freeSoloTag, setFreeSoloTag] = React.useState<boolean>(true);

  const { t } = useWizardTranslation();

  const isStickyFooter = useStickyFooter();
  const { selectedFCDOCountryCode } = useLocationTo();

  // Use the custom hook for handlers and state
  const {
    selectedSTCountry,
    setSelectedSTCountry,
    showSameCountryModal,
    setShowSameCountryModal,
    sameCountrySelected,
    isFCDOCountrySelected,
    setIsFCDOCountrySelected,
    isModalOpen,
    setIsModalOpen,
    tagSearchFieldOnChange,
    handleSearchCountry,
  } = useSingleTripLocationHandlers({
    fromCountryCode,
    setToLocationCountryDetails,
    modifyPayloadToLocation,
    setSelectedState,
  });

  useEffect(() => {
    if (countryArray) {
      setSelectedSTCountry(countryArray);
    }
  }, [countryArray, setSelectedSTCountry]);

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length > 2) {
      setFreeSoloTag(loading);
    } else {
      setFreeSoloTag(true);
    }
  }, [debouncedSearchTerm, loading]);

  const getPopupName = () => {
    if (showSameCountryModal) {
      return sameCountryModalHeaderText ?? "Information";
    }
    if (isFCDOCountrySelected) {
      return fcdoCountryHeaderText;
    }
    return "Information";
  };

  useGtmPopup({
    tripType: QuestionThree,
    isPopupOpen: isFCDOCountrySelected || showSameCountryModal || isModalOpen,
    popupName: getPopupName(),
  });

  return (
    <div
      className=" md:mb-spacing-xxl"
      data-testid="single-trip-support-content"
    >
      <FCDOCountryAndDuplicateCountryModal
        fcdoCountry={isFCDOCountrySelected}
        setFcdoCountry={setIsFCDOCountrySelected}
        isModalOpen={Boolean(isFCDOCountrySelected) || Boolean(isModalOpen)}
        setIsModalOpen={isModalOpen ? setIsModalOpen : setIsFCDOCountrySelected}
        fcdoCountrySubTextText={pageContent?.fcdoCountrySubTextText}
        sameCountrySubTextText={pageContent?.sameCountrySubTextText}
        fcdoCountryBtnText={pageContent?.fcdoCountryBtnText}
        sameCountryBtnText={pageContent?.sameCountryBtnText}
        fcdoCountryHeaderText={pageContent?.fcdoCountryHeaderText}
        sameCountryHeaderText={pageContent?.sameCountryHeaderText}
        customFcdoMessages={pageContent?.customFcdoMessage}
        selectedCountryCode={selectedFCDOCountryCode}
      />

      <SameCountryModal
        isOpen={showSameCountryModal}
        setIsOpen={setShowSameCountryModal}
        modalHeaderText={pageContent?.sameCountryModalHeaderText}
        modalSubText={pageContent?.sameCountryModalSubText}
        agreeButtonText={pageContent?.sameCountryBtnText}
        closeButtonText={pageContent?.closeBtnText}
        sameCountrySelected={sameCountrySelected}
        selectedSTCountry={selectedSTCountry as OptionListProps[]}
        setSelectedSTCountry={setSelectedSTCountry}
        tagSearchFieldOnChange={tagSearchFieldOnChange}
        setIsDisable={setIsDisable}
      />
      {current.uiElementControls?.locationFrom && (
        <LocationFromContainer
          questionOneText={locationFromContent?.questionOneText}
          questionOnePlaceHolder={locationFromContent?.questionOnePlaceHolder}
          setFromCountry={setFromCountry}
          locationFromCountreyClick={() => {
            const element1 = document.getElementById("paragaph-test-id");
            if (element1 && actionButtonRef) {
              handleScrollToRefAnyoneVisiting(actionButtonRef, !isStickyFooter);
            } else {
              const element = document.getElementById("ST-MAIN-HEADER-ID");
              setTimeout(() => {
                element?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }, 100);
            }
          }}
        />
      )}
      <h1
        id="ST-MAIN-HEADER-ID"
        className=" font-header-font  lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
              md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
      >
        {questionTwoText}
      </h1>
      {quoteLoading ? (
        <Loader />
      ) : (
        <button
          id="scrollableInput"
          data-testid="scrollable-input-id"
          type="button"
          onClick={() => {
            // Only scroll on desktop, prevent scroll on tablets/mobile when keyboard opens
            if (isStickyFooter) {
              handleScrollToRef(actionButtonRef, !isStickyFooter);
            }
          }}
          className={` ${styles.country_tag_search_wrapper} lg:w-[414px] md:w-[400px] w-full mx-auto border hover:border-2 !border-interactive-secondary-default min-h-[56px] mt-spacing-l
            rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#368fe9] focus-visible:outline-offset-2`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              // Only scroll on desktop
              if (isStickyFooter) {
                handleScrollToRef(actionButtonRef, !isStickyFooter);
              }
            }
          }}
          tabIndex={0}
        >
          {typeof window !== "undefined" && (
            <TagSearchField
              loading={loading}
              loadingText={t("searching", "Searching...")}
              popularCountries={processCountryList(popularCountryList ?? {})}
              tagFieldPlaceholderClassName="!text-text-placeholder-secondary !font-semibold !font-body-font !text-[16px] leading-[24px]"
              popularCountryTitle={popularCountryHeaderText}
              popularCountryClassName={`text-[18px] cursor-pointer ${getFontClasses(
                brand
              )} !font-normal !leading-[27px]`}
              popularCountryHeaderClassName={`text-[16px] ${getFontClasses(
                brand
              )} !leading-[24px]`}
              optionList={addressesList}
              width="200"
              values={selectedSTCountry as []}
              freeSolo={freeSoloTag}
              $brand={brand}
              noOptionsText={
                addresses?.length === 0
                  ? t("no_result_found", "No result found")
                  : ""
              }
              selectOnFocus={false}
              // add the condtitions which needs to force close the auto complete. ex:when the modal is opened auto complete needs to stay closed
              forceClose={
                showSameCountryModal || isFCDOCountrySelected || isModalOpen
              }
              onChange={(event: unknown, values: unknown) => {
                const selectedValues = values as OptionListProps[];
                handleSearchCountry(selectedValues);
              }}
              onInputChange={(e: unknown, value: unknown) => {
                const query = value as string;
                setSelectedStateTwo(query);
                // Only scroll on desktop, let mobile browsers handle keyboard positioning naturally
                if (isStickyFooter) {
                  scrollInputToTop();
                }
                if (query.length > 2) {
                  setValueFilter(query);
                } else {
                  setFreeSoloTag(true);
                  // clear address list
                  clearAddressList();
                }
                if (!query) {
                  setValueFilter(null);
                }
              }}
              $hasValues={false}
              $width=""
              $alertState="success"
              tagFieldPlaceholderText={questionTwoPlaceHolder}
            />
          )}
        </button>
      )}
    </div>
  );
}

export default withBrand(SingleTripLocation);
